import React, { useState } from 'react';
    import { motion } from 'framer-motion';
    import { useData } from '../context/DataContext';
    import SafeIcon from '../common/SafeIcon';
    import * as FiIcons from 'react-icons/fi';

    const { FiPlus, FiTrash2, FiBookOpen, FiLock, FiEye, FiUsers, FiX } = FiIcons;

    const SubjectManagement = () => {
      const { subjects = [], defaultSubjects = [], addSubject, deleteSubject, students = [] } = useData();
      const [newSubject, setNewSubject] = useState('');
      const [showAddForm, setShowAddForm] = useState(false);
      const [viewingSubject, setViewingSubject] = useState(null);

      const handleAddSubject = async (e) => {
        e.preventDefault();
        if (newSubject.trim()) {
          const result = await addSubject(newSubject.trim());
          if (result.success) {
            setNewSubject('');
            setShowAddForm(false);
          } else {
            alert(result.message);
          }
        }
      };

      const handleDeleteSubject = async (subjectName) => {
        if (window.confirm(`Are you sure you want to delete "${subjectName}"?`)) {
          const result = await deleteSubject(subjectName);
          if (!result.success) {
            alert(result.message);
          }
        }
      };

      const getStudentCount = (subjectName) => {
        return students.filter(student => {
          if (student.subjects && Array.isArray(student.subjects)) {
            return student.subjects.some(sub => sub.subject === subjectName);
          }
          return student.subject === subjectName;
        }).length;
      };

      const getStudentsForSubject = (subjectName) => {
        return students.filter(student => {
          if (student.subjects && Array.isArray(student.subjects)) {
            return student.subjects.some(sub => sub.subject === subjectName);
          }
          return student.subject === subjectName;
        });
      };

      const StudentsModal = ({ subject, onClose }) => {
        const subjectStudents = getStudentsForSubject(subject);
        return (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
            <motion.div initial={{ opacity: 0, scale: 0.95 }} animate={{ opacity: 1, scale: 1 }} className="bg-white rounded-xl shadow-xl max-w-4xl w-full max-h-[90vh] overflow-y-auto">
              <div className="flex items-center justify-between p-6 border-b">
                <h2 className="text-xl font-semibold text-gray-900">Students in {subject}</h2>
                <button onClick={onClose} className="text-gray-400 hover:text-gray-600 transition-colors"><SafeIcon icon={FiX} className="text-xl" /></button>
              </div>
              <div className="p-6">
                {subjectStudents.length > 0 ? (
                  <div className="overflow-x-auto">
                    <table className="w-full">
                      <thead className="bg-gray-50">
                        <tr>
                          <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Student Name</th>
                          <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Grade</th>
                          <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
                        </tr>
                      </thead>
                      <tbody className="bg-white divide-y divide-gray-200">
                        {subjectStudents.map((student, index) => (
                          <tr key={index} className="hover:bg-gray-50">
                            <td className="px-4 py-4 whitespace-nowrap text-sm font-medium text-gray-900">{student.studentName}</td>
                            <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-900">Grade {student.grade}</td>
                            <td className="px-4 py-4 whitespace-nowrap"><span className="px-2 py-1 text-xs font-semibold rounded-full bg-blue-100 text-blue-800">{student.status}</span></td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                ) : (
                  <div className="text-center py-12">
                    <SafeIcon icon={FiUsers} className="text-gray-400 text-4xl mx-auto mb-4" />
                    <p className="text-gray-500">No students found for this subject.</p>
                  </div>
                )}
              </div>
              <div className="flex justify-end gap-3 p-6 border-t">
                <button onClick={onClose} className="px-4 py-2 text-gray-600 border border-gray-300 rounded-lg hover:bg-gray-50">Close</button>
              </div>
            </motion.div>
          </div>
        );
      };

      return (
        <div className="w-full">
          <div className="flex justify-between items-center mb-6">
            <h2 className="text-2xl font-bold text-gray-900">Subject Management</h2>
            <motion.button whileHover={{ scale: 1.05 }} onClick={() => setShowAddForm(true)} className="flex items-center gap-2 bg-indigo-600 text-white px-4 py-2 rounded-lg">
              <SafeIcon icon={FiPlus} /> Add Subject
            </motion.button>
          </div>

          {showAddForm && (
            <motion.div initial={{ opacity: 0, y: -20 }} animate={{ opacity: 1, y: 0 }} className="bg-white rounded-xl shadow-sm p-6 mb-6">
              <form onSubmit={handleAddSubject} className="flex gap-4">
                <input type="text" value={newSubject} onChange={(e) => setNewSubject(e.target.value)} placeholder="Enter new subject name" className="flex-1 px-3 py-2 border rounded-lg outline-none focus:ring-2 focus:ring-indigo-500" required />
                <button type="submit" className="px-4 py-2 bg-indigo-600 text-white rounded-lg">Add Subject</button>
                <button type="button" onClick={() => setShowAddForm(false)} className="px-4 py-2 text-gray-600 border rounded-lg">Cancel</button>
              </form>
            </motion.div>
          )}

          <div className="bg-white rounded-xl shadow-sm overflow-hidden">
            <div className="divide-y divide-gray-200">
              {subjects.map((subject, index) => {
                const isDefault = defaultSubjects.includes(subject);
                const studentCount = getStudentCount(subject);
                return (
                  <motion.div key={subject} initial={{ opacity: 0, x: -20 }} animate={{ opacity: 1, x: 0 }} transition={{ delay: index * 0.05 }} className="px-6 py-4 flex items-center justify-between hover:bg-gray-50">
                    <div className="flex items-center gap-3">
                      <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${isDefault ? 'bg-blue-100' : 'bg-green-100'}`}>
                        <SafeIcon icon={isDefault ? FiLock : FiBookOpen} className={isDefault ? 'text-blue-600' : 'text-green-600'} />
                      </div>
                      <div>
                        <h4 className="text-sm font-medium text-gray-900">{subject}</h4>
                        <p className="text-sm text-gray-500">{isDefault ? 'Default' : 'Custom'} • {studentCount} students</p>
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      <button onClick={() => setViewingSubject(subject)} className="text-indigo-600 hover:text-indigo-900 p-1" title="View Students"><SafeIcon icon={FiEye} /></button>
                      {!isDefault && (
                        <button onClick={() => handleDeleteSubject(subject)} className="text-red-600 hover:text-red-900 p-1" title="Delete Subject" disabled={studentCount > 0}><SafeIcon icon={FiTrash2} /></button>
                      )}
                    </div>
                  </motion.div>
                );
              })}
            </div>
          </div>
          {viewingSubject && <StudentsModal subject={viewingSubject} onClose={() => setViewingSubject(null)} />}
        </div>
      );
    };

    export default SubjectManagement;